import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from "@azure/msal-browser";

export interface AppConfig {
  authMode: "entra" | "dev";
  tenantId: string;
  clientId: string;
  scope: string;
  mock: boolean;
}

export interface Session {
  config: AppConfig;
  userName: string;
  getToken: () => Promise<string | undefined>;
  signOut: () => Promise<void>;
}

/** Signs the user in (redirect) and returns a session, or undefined while a redirect is in flight. */
export async function startSession(): Promise<Session | undefined> {
  const config = (await (await fetch("/api/config")).json()) as AppConfig;
  if (config.authMode === "dev") {
    return {
      config,
      userName: "Utvecklare (lokalt)",
      getToken: async () => undefined,
      signOut: async () => undefined,
    };
  }

  const msal = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin + "/",
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  await msal.initialize();
  const redirect = await msal.handleRedirectPromise();
  const account: AccountInfo | null = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
  if (!account) {
    await msal.loginRedirect({ scopes: [config.scope] });
    return undefined;
  }
  msal.setActiveAccount(account);

  return {
    config,
    userName: account.name ?? account.username,
    getToken: async () => {
      try {
        return (await msal.acquireTokenSilent({ scopes: [config.scope], account })).accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          await msal.acquireTokenRedirect({ scopes: [config.scope], account });
        }
        throw err;
      }
    },
    signOut: () => msal.logoutRedirect({ account }),
  };
}
